import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Stack,
  CircularProgress,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Modal,
  TextField,
  Card,
  CardContent,
  InputAdornment,
} from "@mui/material";
import { toast } from "react-toastify";
import Grid from "@mui/material/Grid";
import { useFetchContext } from "../../../contexts/FetchContext";
import { useInventoryContext } from "../../../contexts/InventoryContext";
import { PageContainer } from "../../shared/components/PageContainer";
import NoResult from "../../shared/components/NoResult";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { OverviewCard } from "../../shared/components/OverviewCard";
import { groupBy } from "lodash";
import {
  FlipToBack,
  FlipToFront,
  CheckCircle,
  Search,
} from "@mui/icons-material";
import ConfirmationModal from "../../shared/components/Modal/ConfirmationModal";
import { useSoundContext } from "../../../contexts/SoundContext";

const RESET_MODAL_TEXT = "Sei sicuro di voler resettare l'inventario?";

const InventoryView = () => {
  const { state, fetchData } = useFetchContext();
  const { play } = useSoundContext();
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const { inventoryData, setProductStocked, resetInventoryData } =
    useInventoryContext();
  const [displayData, setDisplayData] = useState<
    { productCode: string; totalQuantity: number; totalStocked: number }[]
  >([]);
  const [resetModalText, setResetModalText] =
    useState<string>(RESET_MODAL_TEXT);

  const [modalOpen, setModalOpen] = useState(false);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isResetModalOpen, setResetModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [locationSerialNumberValue, setLocationSerialNumberValue] =
    useState("");
  const [locationValue, setLocationValue] = useState("");
  const [locationType, setLocationType] = useState<"matricola" | "articolo">(
    "articolo"
  );

  useEffect(() => {
    const interval = setInterval(() => {
      axios.get("/products/inventory-check").then((response) => {
        const totalStocks = response?.data?.info?.totalStocks;
        if (
          totalStocks &&
          inventoryData?.totalInStock &&
          totalStocks !== inventoryData.totalInStock
        ) {
          setResetModalText(
            `C'è stata una variazione di ${
              totalStocks - inventoryData.totalInStock
            } prodotti. È necessario resettare l'inventario.`
          );
          setResetModalOpen(true);
        }
      });
    }, 5000);
    return () => clearInterval(interval);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (searchTerm: string) => {
    setSearchKeyword(searchTerm);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setLocationValue("");
    setLocationSerialNumberValue("");
    setSelectedProduct("");
  };

  const handleLocationModalSubmit = async () => {
    try {
      if (selectedProduct && locationValue) {
        if (locationType === "matricola") {
          const isValid = inventoryData?.inventoryData?.find(
            (item) =>
              item?.serialNumber === locationSerialNumberValue &&
              selectedProduct === item?.productCode
          );
          const isAlreadyStocked = inventoryData?.inventoryData?.find(
            (item) =>
              item?.serialNumber === locationSerialNumberValue &&
              item?.isStocked
          );
          if (!isAlreadyStocked) {
            handleConfirmSerialNumber(locationSerialNumberValue);
          }
          if (!isValid) {
            toast.error("Matricola non valida");
            play("error");
            handleCloseModal();
            return;
          }
        }
        const payload = {
          serialNumber:
            locationType === "matricola"
              ? locationSerialNumberValue
              : undefined,
          productCode:
            locationType === "articolo" ? selectedProduct : undefined,
          warehouseLocation: locationValue,
        };
        const response = await fetchData(
          "/products/set-location",
          "POST",
          payload
        );
        if (response.data.affected > 0) {
          setProductStocked(payload);
          toast.success(`Prodotti ubicati: ${response.data.affected}`);
        } else {
          toast.error("Operazione fallita: " + response?.data?.message);
        }
        handleCloseModal();
        setSearchKeyword("");
        document.getElementById("search")?.focus();
      }
    } catch (error: any) {
      toast.error("Operazione fallita: " + error?.message);
      console.error("Error submitting modal data:", error);
    }
  };

  const handleConfirmSerialNumber = (serialNumber: string) => {
    const founded = inventoryData?.inventoryData?.find(
      (item) => item?.serialNumber === serialNumber
    );
    if (founded) {
      setProductStocked({
        serialNumber: founded.serialNumber,
        productCode: founded.productCode,
      });
      play("success");
      toast.success(`Prodotto Inventariato: ${founded.productCode}`);
    } else {
      play("error");
      toast.error("Prodotto non trovato");
    }
    setSearchKeyword("");
    document.getElementById("search")?.focus();
  };

  const handleCompleteInventory = async () => {
    try {
      const response = await fetchData("/warehouses/send-inventory", "POST", {
        inventoryData: displayData,
        inventorySerialNumberData: inventoryData?.inventoryData,
      });
      if (response.status >= 400) {
        toast.error("Operazione fallita: " + response?.data?.message);
      } else {
        resetInventoryData();
        toast.success(`Inventario completato e inviato con successo`);
      }
      handleCloseModal();
      setSearchKeyword("");
      document.getElementById("search")?.focus();
    } catch (error: any) {
      console.error("Error submitting modal data:", error);
      toast.error("Operazione fallita: " + error?.message);
    }
    setConfirmationModalOpen(false);
    console.log(displayData);
  };

  useEffect(() => {
    const groupedInventoryData = groupBy(
      inventoryData?.inventoryData,
      "productCode"
    );
    const groupedDisplayData = Object.keys(groupedInventoryData).map((key) => ({
      productCode: key,
      totalQuantity: groupedInventoryData[key].length,
      totalStocked: groupedInventoryData[key].reduce(
        (acc, item) => acc + (item.isStocked ? 1 : 0),
        0
      ),
    }));
    setDisplayData(groupedDisplayData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword, inventoryData]);

  return (
    <PageContainer>
      <Stack>
        <Grid container spacing={1} marginBottom={2}>
          <Grid item xs={12} md={6}>
            <OverviewCard
              sx={{ height: "100%", cursor: "pointer" }}
              value={inventoryData?.totalInStock}
              title="IN GIACENZA"
              backgroundIcon="lightblue"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <OverviewCard
              sx={{ height: "100%", cursor: "pointer" }}
              value={inventoryData?.totalInventory}
              title="INVENTARIATI"
              backgroundIcon="green"
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} sx={{ pb: 2 }}>
            <Card>
              <CardContent>
                <TextField
                  fullWidth
                  autoFocus
                  id="search"
                  label="Cerca per matricola..."
                  value={searchKeyword}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {searchKeyword ? (
                          <CloseRounded
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleSearchChange("")}
                          />
                        ) : (
                          <Search />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  onKeyDown={(event: any) => {
                    if (event.key === "Enter") {
                      handleConfirmSerialNumber(searchKeyword);
                      event.preventDefault();
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          {state.isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "80vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : inventoryData?.totalInStock ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Articolo</TableCell>
                    <TableCell align="right">QTA</TableCell>
                    <TableCell align="right">INVENTARIATI</TableCell>
                    <TableCell align="right">UBICA</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayData
                    ?.sort((itemA, itemB) => {
                      if (itemB.totalStocked !== itemA.totalStocked) {
                        return itemB.totalStocked - itemA.totalStocked;
                      }
                      return itemA.productCode.localeCompare(itemB.productCode);
                    })
                    ?.map((row) => {
                      const quantity = inventoryData?.inventoryData?.filter(
                        (p) => p.productCode === row.productCode
                      )?.length;
                      const inventoryCount =
                        inventoryData?.inventoryData?.filter(
                          (p) =>
                            p.productCode === row.productCode && p.isStocked
                        )?.length;
                      let rowColor = "default";
                      if (quantity === inventoryCount) {
                        rowColor = "darkseagreen";
                      } else if (
                        quantity > inventoryCount &&
                        inventoryCount > 0
                      ) {
                        rowColor = "#f2d49d";
                      }
                      return (
                        <TableRow
                          key={row.productCode}
                          sx={{
                            backgroundColor: rowColor,
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {row.productCode}
                          </TableCell>
                          <TableCell align="right">{quantity}</TableCell>
                          <TableCell align="right">{inventoryCount}</TableCell>
                          <TableCell>
                            <Grid
                              container
                              justifyContent="flex-end"
                              spacing={2}
                            >
                              <Grid item alignItems={"center"}>
                                <Button
                                  disabled={inventoryCount < 1}
                                  variant="text"
                                  color="info"
                                  startIcon={<FlipToFront />}
                                  sx={{ padding: 0 }}
                                  onClick={() => {
                                    setLocationType("matricola");
                                    handleOpenModal();
                                    setSelectedProduct(row.productCode);
                                  }}
                                >
                                  MATRICOLA
                                </Button>
                              </Grid>
                              <Grid item alignItems={"center"}>
                                <Button
                                  disabled={inventoryCount !== quantity}
                                  variant="text"
                                  color="info"
                                  startIcon={<FlipToBack />}
                                  sx={{ padding: 0 }}
                                  onClick={() => {
                                    setLocationType("articolo");
                                    handleOpenModal();
                                    setSelectedProduct(row.productCode);
                                  }}
                                >
                                  ARTICOLO
                                </Button>
                              </Grid>
                            </Grid>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <NoResult />
          )}
        </Grid>
        <Grid container justifyContent="flex-end" spacing={2} my={2}>
          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<CloseRounded />}
              onClick={() => setResetModalOpen(true)}
            >
              RESET
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={() => setConfirmationModalOpen(true)}
            >
              COMPLETA
            </Button>
          </Grid>
        </Grid>

        <Modal disableEnforceFocus open={modalOpen} onClose={handleCloseModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              width: 400,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" mb={2}>
              Inserisci ubicazione {selectedProduct ?? ""}
            </Typography>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleLocationModalSubmit();
              }}
            >
              {locationType === "matricola" && (
                <TextField
                  fullWidth
                  autoFocus
                  label="Matricola"
                  value={locationSerialNumberValue}
                  onChange={(e) => setLocationSerialNumberValue(e.target.value)}
                  onKeyDown={(event: any) => {
                    if (event.key === "Enter") {
                      document.getElementById("ubicazione")?.focus();
                    }
                  }}
                  sx={{ mb: 2 }}
                />
              )}
              <TextField
                fullWidth
                autoFocus={locationType === "articolo"}
                id="ubicazione"
                label="Ubicazione"
                value={locationValue}
                onChange={(e) => setLocationValue(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleCloseModal}>
                  Cancella
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={!locationValue}
                >
                  Conferma
                </Button>
              </Stack>
            </form>
          </Box>
        </Modal>
      </Stack>
      <ConfirmationModal
        title="RESET INVENTARIO"
        isOpen={isResetModalOpen}
        onConfirm={() => {
          handleSearchChange("");
          resetInventoryData();
          setResetModalOpen(false);
          setResetModalText(RESET_MODAL_TEXT);
        }}
        onClose={() => setResetModalOpen(false)}
        isLoading={state?.isLoading}
        text={resetModalText}
      />
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onConfirm={handleCompleteInventory}
        onClose={() => setConfirmationModalOpen(false)}
        isLoading={state?.isLoading}
        text={`Hai caricato ${inventoryData?.totalInventory}/${inventoryData?.totalInStock} articoli. Sei sicuro di voler completare l'inventario?`}
      />
    </PageContainer>
  );
};

export default InventoryView;
